import { queryOptions } from "@tanstack/react-query";
import type { SessionData } from "~/app/store/bffApi";
import { fetchPageData, getPageDataErrorCode, PageDataRequestError } from "./pageDataClient";
import { pageDataKeys } from "./pageDataKeys";
import { queryClient } from "~/app/queryClient";
import type { SessionUserPreferences } from "~/domains/user/types/SessionUserData";
import { isAccountContextTransitionInProgress } from "~/app/accountContextTransitionState";

const DUPLICATE_CART_INVARIANT_STATUS = 409;
export const MULTIPLE_VALID_CARTS_FOR_CONTEXT = "MULTIPLE_VALID_CARTS_FOR_CONTEXT";
export const PRICE_REFERENCE_CONTEXT_INVALID = "PRICE_REFERENCE_CONTEXT_INVALID";

class AccountContextTransitionPendingError extends Error {}

export const sessionQueryOptions = (
  options: { allowDuringAccountTransition?: boolean } = {},
) =>
  queryOptions({
    queryKey: pageDataKeys.session(),
    queryFn: () => {
      if (isAccountContextTransitionInProgress() && !options.allowDuringAccountTransition) {
        throw new AccountContextTransitionPendingError();
      }
      return fetchPageData<SessionData>("/page-data/session");
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) =>
      !(error instanceof AccountContextTransitionPendingError) &&
      !isSessionDuplicateInvariantError(error) &&
      !isPriceReferenceContextInvalidError(error) &&
      failureCount < 1,
  });

/**
 * Session Data has one deliberate 409 contract: strict duplicate Valid Cart discovery.
 * It is an authenticated invariant breach, not session expiry and not stale Session Data.
 */
export function isSessionDuplicateInvariantError(error: unknown): error is PageDataRequestError {
  return (
    error instanceof PageDataRequestError &&
    error.status === DUPLICATE_CART_INVARIANT_STATUS &&
    getPageDataErrorCode(error) === MULTIPLE_VALID_CARTS_FOR_CONTEXT
  );
}

export function isPriceReferenceContextInvalidError(error: unknown): error is PageDataRequestError {
  return error instanceof PageDataRequestError && getPageDataErrorCode(error) === PRICE_REFERENCE_CONTEXT_INVALID;
}

export function invalidateSession(): void {
  void queryClient.invalidateQueries({ queryKey: pageDataKeys.session() });
}

export function patchSessionUserPreferences(updates: Partial<SessionUserPreferences>): void {
  queryClient.setQueryData<SessionData>(pageDataKeys.session(), (session) => {
    if (!session) {
      return session;
    }

    return {
      ...session,
      user: {
        ...session.user,
        preferences: {
          ...session.user.preferences,
          ...updates,
        },
      },
    };
  });
}
